import { Edit, X } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import { PermissionGate } from "../../../../components/PermissionGate";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../../components/ui/sheet";
import { type AuditSession } from "../../../../models/audit/auditSession";
import { useAudit } from "../../../../contexts/auditContext";
import { AnalyticsWorkflowError } from "../../../../lib/analyticsWorkflow";
import { useAnalyticsResultData } from "../../../../hooks/audit/useAnalyticsResultData";
import { useColumnActionLogs } from "../../../../hooks/useColumnActionLogs";
import { ActivityLogSection } from "./attributeTestAnalysis/ActivityLogSection";
import { analyticsErrorMessage } from "./analyticsResultAnalysis/analyticsErrorMessage";
import { AnalyticsReviewSections } from "./analyticsResultAnalysis/AnalyticsReviewSections";
import { AnalyticsOverrideDialog } from "./analyticsResultAnalysis/AnalyticsOverrideDialog";
import {
  outcomeBadgeStyle,
  outcomeBadgeValue,
} from "./analyticsResultAnalysis/outcomeBadge";
import { useAnalyticsOverrideFlow } from "../../../../hooks/audit/useAnalyticsOverrideFlow";
import { VerdictProvenance } from "./analyticsResultAnalysis/VerdictProvenance";

interface AnalyticsResultAnalysisProps {
  auditSession: AuditSession;
  checkResultId?: string;
  sourceRowId?: string;
  sampleNumber?: number | null;
  onClose: () => void;
  handleCheckSelection: (selection: {
    sourceRowId?: string;
    checkResultId?: string;
    evalColumnId?: string;
  }) => void;
}

/**
 * The sheet an analytics cell opens where no fold-out does: the verdict's
 * header — its answer and its sample — over
 * `AnalyticsReviewSections`, with the collapsed `Activity` log beneath them and
 * the `New Conclusion` override that writes to both.
 *
 * A sibling of the attribute-test panel rather than an arm inside it. The two
 * verdicts share a badge vocabulary, but that panel's layout is an agent's trail
 * beside a citation table, and an analytics verdict's evidence is a run: the
 * pictures of the steps it took, the files it was handed, the files it
 * published, and the workflow that did it.
 *
 * The sections themselves are not this component's: the fold-out's
 * `Workpaper Review` tab shows the same review without a sheet around it, so
 * the stack lives where both can render it.
 */
export const AnalyticsResultAnalysis = ({
  auditSession,
  checkResultId,
  sourceRowId,
  sampleNumber,
  onClose,
  handleCheckSelection,
}: AnalyticsResultAnalysisProps) => {
  const { controlSession } = useAudit();
  const isSessionComplete = controlSession?.status === "complete";

  const {
    analyticsResultId,
    analyticsResult,
    isLoading,
    error,
    refetch,
    column,
    columnName,
  } = useAnalyticsResultData({ auditSession, checkResultId });

  const {
    logs: activityLogs,
    loading: logsLoading,
    fetchLogs: refetchActivityLogs,
  } = useColumnActionLogs({ analyticsResultId, fetchOnLoad: true });

  const overrideFlow = useAnalyticsOverrideFlow({
    auditSession,
    analyticsResultId,
    analyticsResult,
    column,
    checkResultId,
    sourceRowId,
    refetch,
    // The work-paper owns this pane's selection wiring, so following the
    // successor is a call back into it rather than a write this pane makes.
    onRetarget: handleCheckSelection,
    refetchActivityLogs,
  });

  /**
   * The pane behind the dialog, in whichever state its detail fetch is in.
   *
   * The dialog is mounted beside it rather than inside these states: `refetch`
   * flips the cache entry to loading and notifies synchronously, so a dialog
   * living in here would be torn down for the length of the superseded path's
   * re-read, taking the auditor's typed conclusion and their caret with it, and
   * would never come back if that re-read failed.
   */
  const renderPane = () => {
    if (isLoading) {
      return (
        <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
          <SheetContent
            side="right"
            hideClose
            className="w-full sm:max-w-[720px]"
          >
            <SheetHeader>
              <SheetTitle>Analytics Result</SheetTitle>
            </SheetHeader>
            <div className="flex items-center justify-center h-40">
              <LoadingSpinner />
            </div>
          </SheetContent>
        </Sheet>
      );
    }

    if (error) {
      // A schema violation re-parses the same payload and fails the same way,
      // so Retry is the one offer known not to clear it. The copy for this kind
      // asks for a refresh instead, and a refresh is what can change the
      // outcome: the row is read again from scratch, under whatever client
      // build is current by then.
      const unreadable =
        error instanceof AnalyticsWorkflowError && error.kind === "unreadable";

      return (
        <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
          <SheetContent
            side="right"
            hideClose
            className="w-full sm:max-w-[720px]"
          >
            <SheetHeader>
              <SheetTitle>Analytics Result</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              <p className="text-sm">{analyticsErrorMessage(error)}</p>
              {unreadable ? (
                <Button
                  variant="secondary"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
              ) : (
                /* Retry re-runs the fetch, which clears a transient failure; a
                   standing refusal — a deleted workflow, a denied permission —
                   answers the same way each time, so this is offered without
                   promising it always recovers. */
                <Button variant="secondary" onClick={() => refetch()}>
                  Retry
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>
      );
    }

    if (!analyticsResult) {
      return (
        <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
          <SheetContent
            side="right"
            hideClose
            className="w-full sm:max-w-[720px]"
          >
            <SheetHeader>
              <SheetTitle>Analytics Result</SheetTitle>
            </SheetHeader>
            <p className="mt-4 text-sm">No analytics result found.</p>
          </SheetContent>
        </Sheet>
      );
    }

    if (!column) {
      return (
        <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
          <SheetContent
            side="right"
            hideClose
            className="w-full sm:max-w-[720px]"
          >
            <SheetHeader>
              <SheetTitle>Analytics Result</SheetTitle>
            </SheetHeader>
            <p className="mt-4 text-sm">
              This verdict loaded, but its analytics column is not part of this
              procedure.
            </p>
          </SheetContent>
        </Sheet>
      );
    }

    // Only the label: the chip states the pill's answer, not its tone.
    const outcomeLabel = outcomeBadgeStyle(
      outcomeBadgeValue(analyticsResult),
    ).label;

    return (
      <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          hideClose
          className="w-full p-0 sm:max-w-[720px]"
        >
          <div className="h-screen flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <div className="flex items-center gap-3">
                <img src="/logo_orange_bg.svg" alt="logo" className="w-6" />
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <SheetHeader>
                      <SheetTitle>Analytics Result</SheetTitle>
                    </SheetHeader>
                    <Badge variant="secondary">{outcomeLabel}</Badge>
                  </div>
                  <div className="text-xs text-font-secondary">
                    {(sampleNumber ? `Sample ${sampleNumber}` : "Sample") +
                      (columnName ? ` · ${columnName}` : "")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isSessionComplete &&
                  analyticsResult.supersededAt === null && (
                    /* Gated on liveness because the override route refuses
                       a superseded verdict with a 409, and this pane is
                       reachable for a superseded row — history is addressed
                       by id, and a write whose successor did not parse leaves
                       the panel pointed at the row it just superseded.

                       Gated on permission because the server gates it: the
                       override endpoint this button posts to requires the
                       `run_test` permission, as does every write route on the
                       analytics and attribute-test result APIs. Without the
                       gate the button is offered to a reader who can only be
                       refused by the server after they have written their
                       conclusion. */
                    <PermissionGate permission="run_test">
                      <Button
                        variant="secondary"
                        onClick={overrideFlow.open}
                        className="flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        New Conclusion
                      </Button>
                    </PermissionGate>
                  )}
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {/* The only place supersession is stated. Without it the pane
                  reads as fully live while simply offering no way to conclude,
                  which is indistinguishable from a missing permission or a
                  completed session — and it is what the override gate above
                  keys on, so the absent button needs its reason on screen. */}
              {analyticsResult.supersededAt !== null && (
                <div className="border border-stone-300 rounded-md p-4">
                  <div className="text-sm text-font-secondary font-mono mb-1">
                    Superseded
                  </div>
                  <div className="text-sm">
                    A later conclusion replaced this one. This verdict is kept
                    as history and cannot be concluded on.
                  </div>
                </div>
              )}

              <AnalyticsReviewSections
                auditSession={auditSession}
                analyticsResult={analyticsResult}
                column={column}
                sourceRowId={sourceRowId}
                provenance={<VerdictProvenance result={analyticsResult} />}
                activityLog={
                  <ActivityLogSection
                    activityLogs={activityLogs}
                    logsLoading={logsLoading}
                  />
                }
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  };

  return (
    <>
      {renderPane()}

      <AnalyticsOverrideDialog flow={overrideFlow} />
    </>
  );
};
